import Link from 'next/link';
import { FC } from 'react';
import ReactImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import colors from '@pickles/shared/utils/colors';

import { Layout, Title } from '../../components';
import MainBanner from '../../components/MainBanner';
import MetaTags from '../../components/Metatags';
import Container from '../../components/Shared/Container';
import { HistoryLine } from '../../components/Shared/HistoryLine';
import StyledReactMarkdown from '../../components/Shared/StyledReactMarkdown';
import { Video } from '../../components/Shared/Video';
import { VideoItem } from '../../components/Shared/VideosGrid/styles';
import withStrapiDataValidator from '../../hocs/withStrapiDataValidator';
import useScreenSize from '../../hooks/useScreenSize';
import sizes from '../../util/sizes';
import { EOIResponse, getExpressionOfInterest } from '../api/eoi';
import { FooterResponse } from '../api/footer';

export const getServerSideProps = async (context: any) => {
  const { eoiSlug } = context.query;

  let data: EOIResponse | object = {};
  try {
    data = await getExpressionOfInterest(eoiSlug);
  } catch (error) {
    data = {};
  }
  return {
    props: {
      data,
    },
  };
};

interface ExpressionOfInterestProps {
  footerData: FooterResponse;
  data: EOIResponse;
}

const ExpressionOfInterest: FC<ExpressionOfInterestProps> = (props) => {
  const footerData = props?.footerData?.data?.attributes;
  const data = props?.data?.data?.attributes;
  const { minWidth } = useScreenSize();
  const { banner, title, description, downloadFormBtn, imageGalleryBtn, items, videos, seo } = data;

  return (
    <Layout footerData={footerData} activeNavIndex={0} title="">
      <MetaTags title={title} seo={seo} />

      <HistoryLine routeTitle={title} />

      <MainBanner banners={[banner]} />

      <Container mx="auto" maxWidth={sizes.XL}>
        <Container my="78px" px={minWidth.xl ? '170px' : minWidth.s ? '100px' : '20px'}>
          <StyledReactMarkdown text={description} />
        </Container>

        {downloadFormBtn?.url && (
          <Link href={downloadFormBtn?.url} passHref>
            <LinkButton>{downloadFormBtn?.text}</LinkButton>
          </Link>
        )}

        <ItemHolder>
          {items?.map((item) => (
            <div key={item.id}>
              <ImageContainer onClick={(e) => e.stopPropagation()}>
                {item.images ? (
                  <ReactImageGallery
                    items={item.images.data.map((image) => ({ original: image.attributes?.url }))}
                    showThumbnails={false}
                    showNav={true}
                    showPlayButton={false}
                    showFullscreenButton={false}
                  />
                ) : (
                  <Skeleton height={'100%'} width={'100%'} style={{ zIndex: 0 }} />
                )}
              </ImageContainer>
              <Container mt="21px">
                <StyledReactMarkdown text={item.text} />
              </Container>
            </div>
          ))}
        </ItemHolder>

        {imageGalleryBtn?.url && (
          <Container mb="150px">
            <Link href={imageGalleryBtn?.url} passHref>
              <LinkButton>{imageGalleryBtn?.text}</LinkButton>
            </Link>
          </Container>
        )}

        {videos?.items && videos.items.length > 0 && (
          <VideoHolder id="video-holder">
            {videos?.items?.map((videoItem) => (
              <VideoItem key={videoItem.id}>
                <Video
                  source={videoItem.source?.data?.attributes.url}
                  bgImage={videoItem.bgImage?.data?.attributes.url}
                />
                <div>
                  <Title
                    text={videoItem.title}
                    fontSize={24}
                    fontFamily={'BasierCircle'}
                    margin="24px 0 12px 0"
                    lineHeight="150%"
                  />
                </div>
              </VideoItem>
            ))}
          </VideoHolder>
        )}
      </Container>
    </Layout>
  );
};

export default withStrapiDataValidator(ExpressionOfInterest);

const LinkButton = styled.a`
  width: 370px;
  height: 52px;
  background: ${colors.blumine_main_blue};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: ${colors.white};
  font-weight: 600;
`;

const Holder = styled.div`
  margin-top: 81px;
  margin-bottom: 77px;
  display: grid;
  column-gap: 30px;
  row-gap: 45px;
  margin-right: 30px;
  margin-left: 30px;
`;

const ItemHolder = styled(Holder)`
  grid-template-columns: repeat(3, 1fr);
  @media (max-width: ${sizes.L}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${sizes.S}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const VideoHolder = styled(Holder)`
  margin-top: 0;
  grid-template-columns: repeat(2, 1fr);
  @media (max-width: ${sizes.S}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ImageContainer = styled.div`
  display: flex;
  height: 280px;
  max-height: 20.4vw;
  width: 100%;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  @media (max-width: ${sizes.L}) {
    height: initial;
  }
  @media (max-width: ${sizes.S}) {
    max-height: 280px;
  }
  overflow: hidden;
  z-index: 1;
`;
